import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useChannelSubscription, type ChannelSubscription } from '../composables/useChannelSubscription'

export type SourceStatus = 'queued' | 'running' | 'succeeded' | 'failed'

export interface SourceResult {
  routing?: string
  answer?: string | null
  citations?: Array<{ path: string }>
  pages_touched?: string[]
  // Present on a no-op ingest: the model's explanation of why
  // nothing was stored, so the drawer can show the reason rather than a
  // bare "Done".
  note?: string | null
  // Present when the prompt set, snoozed or closed a reminder instead of
  // touching the wiki: the confirmation line, or why it failed.
  reminder?: { confirmation?: string; error?: string } | null
}

export interface SourceRow {
  id: number
  kind: string
  status: SourceStatus | null
  content: string | null
  url: string | null
  result: SourceResult | null
  error: string | null
  created_at: string
  updated_at: string | null
}

// `Broadcastable` (the Ruby concern) wraps the serialized record
// under a `record` key — see `app/models/concerns/broadcastable.rb`.
interface SourceChannelEvent {
  event: 'created' | 'updated'
  record: SourceRow
}

const TERMINAL_WINDOW_HOURS = 24

/**
 * Owns the drawer's view of Source activity: the list of currently-
 * active ingestions (queued/running) and the recently-terminal ones
 * (succeeded/failed within the last 24h). Replaces the localStorage
 * refresh-recovery path that lived in stores/prompts — the server is
 * now the source of truth, and this store mirrors it.
 *
 * Subscription lifecycle: opened on admin sign-in (HomeView), closed
 * on sign-out. The class-level "sources:all" stream delivers every
 * lifecycle event regardless of where the Source was created (drawer,
 * Discord, CLI, Lint), so the header indicator dot stays truthful.
 */
export const useSourceActivityStore = defineStore('source_activity', () => {
  const active = ref<SourceRow[]>([])
  const recentTerminal = ref<SourceRow[]>([])
  const loading = ref(false)
  const loadError = ref<string | null>(null)
  // Updated whenever an in-flight row reaches a terminal status during
  // the live session — not on initial load. HomeView watches this to
  // raise a toast so the user learns ingest finished without having to
  // re-open the drawer. Only the latest completion is held; consumers
  // should treat each ref change as a discrete event.
  const lastCompleted = ref<SourceRow | null>(null)
  let channelSub: ChannelSubscription | null = null

  const hasActive = computed(() => active.value.length > 0)

  // Queue depth = rows still waiting for a worker. Excludes `running`
  // rows because those have already been picked up. ActivityRow shows
  // "Position N of M" when M > 1, so depth and position live next to
  // each other on the store rather than being recomputed in every view
  // that renders an activity list.
  const queuedRowIds = computed(() => active.value.filter((r) => r.status === 'queued').map((r) => r.id))
  const queueDepth = computed(() => queuedRowIds.value.length)
  function queuePositionFor(id: number): number | undefined {
    const idx = queuedRowIds.value.indexOf(id)
    return idx === -1 ? undefined : idx + 1
  }

  async function load(): Promise<void> {
    loading.value = true
    loadError.value = null
    try {
      const since = new Date(Date.now() - TERMINAL_WINDOW_HOURS * 3600 * 1000).toISOString()
      const [activeRows, terminalRows] = await Promise.all([fetchSlice('active'), fetchSlice('terminal', since)])
      active.value = activeRows
      recentTerminal.value = terminalRows
    } catch (e) {
      loadError.value = e instanceof Error ? e.message : 'failed to load activity'
    } finally {
      loading.value = false
    }
  }

  function subscribe(): void {
    if (channelSub) return
    channelSub = useChannelSubscription<SourceChannelEvent>('SourceChannel', {
      received: (data) => handleEvent(data),
      // ActionCable auto-reconnects on transient WS drops but does NOT
      // replay broadcasts emitted during the disconnect window. Without
      // this catch-up, a `running → succeeded` transition that lands
      // mid-reconnect is lost forever and the Add-to-wiki dot stays on
      // until the user refreshes (symptom B). Re-load() on
      // every reconnect rebuilds the active/terminal lists wholesale.
      // skip-first-then-refetch is owned by the composable now.
      refetchOnReconnect: () => void load(),
    })
  }

  function unsubscribe(): void {
    channelSub?.unsubscribe()
    channelSub = null
    active.value = []
    recentTerminal.value = []
    loadError.value = null
  }

  function handleEvent(data: SourceChannelEvent): void {
    const record = data.record
    // `wasActive` lets the lastCompleted assignment below distinguish a
    // genuine in-session transition (queued/running → succeeded/failed)
    // from a row that was already terminal when the broadcast arrived
    // (e.g. a CLI-triggered ingest the SPA never saw start).
    const wasActive = active.value.some((r) => r.id === record.id)
    const existing = findRow(record.id)
    const merged: SourceRow = existing
      ? {
          ...existing,
          status: record.status,
          result: record.result,
          error: record.error,
          content: record.content ?? existing.content,
          url: record.url ?? existing.url,
          updated_at: record.updated_at,
        }
      : record

    removeRow(record.id)
    if (isActive(record.status)) {
      active.value = [merged, ...active.value]
    } else {
      recentTerminal.value = [merged, ...recentTerminal.value]
      if (wasActive) lastCompleted.value = merged
    }
  }

  function findRow(id: number): SourceRow | undefined {
    return active.value.find((r) => r.id === id) ?? recentTerminal.value.find((r) => r.id === id)
  }

  function removeRow(id: number): void {
    active.value = active.value.filter((r) => r.id !== id)
    recentTerminal.value = recentTerminal.value.filter((r) => r.id !== id)
  }

  return {
    active,
    recentTerminal,
    loading,
    loadError,
    hasActive,
    lastCompleted,
    queueDepth,
    queuePositionFor,
    load,
    subscribe,
    unsubscribe,
  }
})

async function fetchSlice(status: 'active' | 'terminal', since?: string): Promise<SourceRow[]> {
  const params = new URLSearchParams({ status })
  if (since) params.set('since', since)
  const res = await fetch(`/api/v1/sources?${params.toString()}`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string }
    throw new Error(body.error || `failed to load ${status} sources (${res.status})`)
  }
  return (await res.json()) as SourceRow[]
}

function isActive(status: SourceStatus | null): boolean {
  return status === 'queued' || status === 'running'
}
